/**
 * F-047: Mutation hook for shipping an ordered order (ordered -> shipped).
 *
 * Wraps `shipOrderTransition` (F-023 alias). On success (result.success is
 * true): updates the detail cache and invalidates the store order list.
 * On business failure or network error: fires a toast with the mapped key.
 */
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { shipOrderTransition, mapTransitionError, useTranslation } from '@dukanti/shared';
import type { ShipTransitionResult } from '@dukanti/shared/types/order';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

export interface UseShipOrderOptions {
  readonly orderId: string;
  readonly storeId: string | null;
}

/**
 * Mark an ordered order as shipped.
 *
 * On success: `setQueryData` from RPC's returned order row + invalidate list.
 * On business failure or network error: fires `toast.error` with the mapped key.
 */
export function useShipOrder(
  opts: UseShipOrderOptions,
): UseMutationResult<ShipTransitionResult, unknown, void> {
  const { orderId, storeId } = opts;
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation<ShipTransitionResult, unknown, void>({
    mutationFn: () => shipOrderTransition(supabase, orderId),
    onSuccess: (result) => {
      if (!result.success) {
        const key = mapTransitionError(undefined, result, 'ship');
        toast.error(t(key));
        return;
      }

      if (result.order) {
        queryClient.setQueryData(
          ['admin-order-detail', orderId],
          (old: { order: unknown; items: unknown; events: unknown } | undefined) =>
            old ? { ...old, order: result.order } : old,
        );
      }
      if (storeId) {
        void queryClient.invalidateQueries({ queryKey: ['store-orders', storeId] });
      }
      void queryClient.invalidateQueries({ queryKey: ['incoming-orders-count-user'] });
      toast.success(t('admin.orders.actions.successToasts.shipped'));
    },
    onError: (err) => {
      const key = mapTransitionError(err, undefined, 'ship');
      toast.error(t(key));
    },
  });
}
